import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";

import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { CLINIC } from "@/constants/site";
import { TESTIDS } from "@/constants/testIds";

const schema = z.object({
  name: z.string().min(2, "Informe seu nome"),
  email: z.string().email("E-mail inválido"),
  phone: z.string().min(8, "Informe seu telefone"),
  pet_name: z.string().optional(),
  exam_type: z.string().optional(),
  message: z.string().min(10, "Descreva um pouco melhor sua solicitação"),
});

const exams = [
  "Ressonância Magnética",
  "Tomografia Computadorizada",
  "Ultrassonografia",
  "Ecodopplercardiograma",
  "Raio-X Digital",
  "Coleta Laboratorial",
  "Outro",
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", phone: "", pet_name: "", exam_type: "", message: "" },
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const { data } = await api.post("/contact", values);
      toast.success(data.message || "Mensagem enviada!");
      reset();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Fale Conosco"
        description="Entre em contato com a Visiovet em Belo Horizonte. Formulário, WhatsApp, telefone e mapa da clínica."
        path="/contato"
      />

      <section className="bg-white section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-brand-600 font-semibold text-sm uppercase tracking-wider mb-3">Fale conosco</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Vamos conversar sobre o exame do seu paciente
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Envie sua mensagem e retornaremos em breve. Para agendamentos, o WhatsApp costuma ser mais rápido.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-200 shadow-sm p-8 sm:p-10">
            <form data-testid={TESTIDS.contactForm} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name">Seu nome *</Label>
                  <Input id="name" data-testid={TESTIDS.contactName} className="mt-1.5" {...register("name")} />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input id="email" data-testid={TESTIDS.contactEmail} type="email" className="mt-1.5" {...register("email")} />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Telefone / WhatsApp *</Label>
                  <Input id="phone" data-testid={TESTIDS.contactPhone} className="mt-1.5" {...register("phone")} />
                  {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone.message}</p>}
                </div>
                <div>
                  <Label htmlFor="pet_name">Nome do pet</Label>
                  <Input id="pet_name" data-testid={TESTIDS.contactPet} className="mt-1.5" {...register("pet_name")} />
                </div>
              </div>

              <div>
                <Label>Tipo de exame</Label>
                <Select onValueChange={(v) => setValue("exam_type", v)}>
                  <SelectTrigger data-testid={TESTIDS.contactExam} className="mt-1.5">
                    <SelectValue placeholder="Selecione um exame (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {exams.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">Mensagem *</Label>
                <Textarea id="message" data-testid={TESTIDS.contactMessage} rows={5} className="mt-1.5" {...register("message")} />
                {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid={TESTIDS.contactSubmit}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 text-white font-semibold px-7 py-3.5 shadow-lg shadow-brand-500/30 hover:bg-brand-600 disabled:opacity-60 transition-colors"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Enviar mensagem
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200 p-8 space-y-5">
              <InfoRow icon={MapPin} title="Endereço" text={CLINIC.address} />
              <InfoRow icon={Phone}  title="Telefones" text={`${CLINIC.phoneMobile} · ${CLINIC.phoneLandline}`} />
              <InfoRow icon={Mail}   title="E-mail" text={CLINIC.email} />
              <InfoRow icon={Clock}  title="Horário" text={`${CLINIC.hours.weekdays} · ${CLINIC.hours.saturday}`} />
            </div>
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Mapa Visiovet"
                src={CLINIC.mapEmbed}
                className="w-full h-80 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoRow({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
        <div className="text-slate-800 mt-1 text-sm leading-relaxed">{text}</div>
      </div>
    </div>
  );
}
